import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { CardData } from '../types'
import { cn } from '@/lib/utils'
import {
    fantasyPool,
    noirPool,
    sunsetPool
} from '../data/cards'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'

interface CatalogProps {
    onClose: () => void
    selectedTheme: 'FANTASY' | 'NOIR' | 'SUNSET'
}

export function CardCatalog({ onClose, selectedTheme: initialTheme }: CatalogProps) {
    const [activeTab, setActiveTab] = useState<'FANTASY' | 'NOIR' | 'SUNSET'>(initialTheme)
    const [hoveredCard, setHoveredCard] = useState<CardData | null>(null)

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleEsc)
        return () => window.removeEventListener('keydown', handleEsc)
    }, [onClose])

    const factions = [
        { id: 'FANTASY' as const, name: 'Bylina of Bones', pool: fantasyPool, color: 'text-emerald-500' },
        { id: 'NOIR' as const, name: 'Midnight Syndicate', pool: noirPool, color: 'text-zinc-400' },
        { id: 'SUNSET' as const, name: 'Sunset Investigations', pool: sunsetPool, color: 'text-violet-500' },
    ]

    const activeFaction = factions.find(f => f.id === activeTab)!

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center p-8 overflow-hidden"
        >
            <div className="w-full max-w-7xl h-full flex flex-col gap-8 relative">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-white/5 pb-6">
                    <div className="flex flex-col">
                        <h2 className="text-4xl font-action tracking-widest text-white leading-none">CATALOG</h2>
                    </div>

                    <div className="flex items-center gap-4">
                        {factions.map(f => (
                            <button
                                key={f.id}
                                onClick={() => setActiveTab(f.id)}
                                className={cn(
                                    "px-6 py-2 text-[10px] font-black tracking-widest uppercase transition-all duration-300 border-b-2",
                                    activeTab === f.id
                                        ? "border-white text-white"
                                        : "border-transparent text-white/20 hover:text-white/40"
                                )}
                            >
                                {f.name}
                            </button>
                        ))}
                    </div>

                    <Button
                        variant="ghost"
                        onClick={onClose}
                        className="text-[10px] font-black tracking-widest uppercase text-white/40 hover:text-white transition-colors"
                    >
                        Close Catalog [Esc]
                    </Button>
                </div>

                {/* Content Area */}
                <div className="flex-1 flex gap-12 overflow-hidden">
                    {/* Grid */}
                    <div className="flex-1 overflow-hidden">
                        <ScrollArea className="h-full pr-6">
                            <div className="grid grid-cols-4 lg:grid-cols-6 gap-6 pb-20 pt-4 px-2">
                                {activeFaction.pool.map((card) => (
                                    <motion.div
                                        key={card.id}
                                        layoutId={card.id}
                                        onMouseEnter={() => setHoveredCard(card)}
                                        className={cn(
                                            "group relative aspect-square bg-white/5 rounded-sm overflow-hidden border border-white/5 cursor-pointer transition-all duration-300",
                                            "hover:border-white/20 hover:scale-105 hover:z-20",
                                            hoveredCard?.id === card.id && "shadow-[0_0_30px_rgba(255,255,255,0.1)]"
                                        )}
                                    >
                                        {card.imageUrl && (
                                            <img
                                                src={card.imageUrl}
                                                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                                                alt={card.name}
                                            />
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                                        <div className="absolute bottom-2 left-2 right-2">
                                            <div className="text-[9px] font-black text-white truncate drop-shadow-md uppercase">
                                                {card.name}
                                            </div>
                                            <div className="flex items-center justify-between mt-1 opacity-60">
                                                <span className="text-[8px] font-bold text-white/50">{card.cost} Energy</span>
                                                {card.rarity === 'legendary' && (
                                                    <span className="text-[7px] font-black p-0.5 px-1 bg-yellow-500/20 text-yellow-500 rounded-[1px]">LEGEND</span>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>

                    {/* Details Sidebar */}
                    <div className="w-80 flex flex-col gap-6 shrink-0 pt-4">
                        <AnimatePresence mode="wait">
                            {hoveredCard ? (
                                <motion.div
                                    key={hoveredCard.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    className="flex flex-col gap-6"
                                >
                                    <div className="aspect-square w-full bg-zinc-900 border border-white/10 rounded-sm overflow-hidden shadow-2xl relative">
                                        {hoveredCard.imageUrl && (
                                            <img src={hoveredCard.imageUrl} className="w-full h-full object-cover" alt="" />
                                        )}
                                        <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center border border-white/10">
                                            <span className="text-sm font-action text-white">{hoveredCard.cost}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-4 px-2">
                                        <div>
                                            <h3 className="text-2xl font-theme italic font-black text-white leading-none uppercase">{hoveredCard.name}</h3>
                                            <p className="text-[10px] font-black tracking-widest text-white/40 uppercase mt-2">{hoveredCard.type}</p>
                                        </div>

                                        <div className="flex gap-4">
                                            {hoveredCard.power !== null && (
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-white/30 uppercase">Power</span>
                                                    <span className="text-2xl font-action text-white">{hoveredCard.power}</span>
                                                </div>
                                            )}
                                            {hoveredCard.health !== null && (
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-white/30 uppercase">Health</span>
                                                    <span className="text-2xl font-action text-white">{hoveredCard.health}</span>
                                                </div>
                                            )}
                                        </div>

                                        {hoveredCard.keywords.length > 0 && (
                                            <div className="flex flex-wrap gap-2">
                                                {hoveredCard.keywords.map(kw => (
                                                    <span key={kw} className="text-[8px] font-black px-2 py-0.5 bg-white/5 border border-white/10 text-white/60 uppercase rounded-sm">
                                                        {kw}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <p className="text-xs leading-relaxed text-white/50 font-medium italic">
                                            "{hoveredCard.description}"
                                        </p>
                                    </div>
                                </motion.div>
                            ) : (
                                <div className="h-full flex items-center justify-center border-l border-white/5 p-8 text-center">
                                    <p className="text-[10px] font-black tracking-[0.4em] text-white/10 uppercase leading-relaxed">
                                        Hover over a card to view ancient recordings and classified data
                                    </p>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}
